import { GameState, PlayerColor } from './types';
import { HOME_SLOTS, stepsToCoord, cellToSteps } from './board';
import {
  createInitialState, rollDice, finishRoll,
  applyMove, nextTurn, tickAnim,
} from './game';
import { pickAiMove } from './ai';
import { renderFrame, readTheme, computeLayout, Layout, DebugDrag, menuRects } from './renderer';
import { attachTapListener, tapToCell, tapOnDice, tapOnRestart, tapOnDebug } from './input';
import { track } from './analytics';
import diceSoundUrl    from '../sfx/dice_new.mp3';
import pieceMoveUrl   from '../sfx/piece_move.mp3';
import pieceOutUrl    from '../sfx/piece_out.mp3';
import pieceAtHomeUrl from '../sfx/piece_at_home.mp3';

// ── Звук (Web Audio API) ──────────────────────────────────────────────────
// Каждый playSfx() создаёт новый независимый узел → параллельное воспроизведение,
// нет конфликтов с seek/promise, работает корректно на мобильных Safari.
const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)() as AudioContext;

// iOS/macOS требует resume после жеста пользователя
const resumeCtx = () => { audioCtx.resume(); };
document.addEventListener('touchstart', resumeCtx, { once: true });
document.addEventListener('click',      resumeCtx, { once: true });

const sfxBuffers = new Map<string, AudioBuffer>();

async function loadSfx(url: string): Promise<AudioBuffer | null> {
  try {
    const resp = await fetch(url);
    const arr  = await resp.arrayBuffer();
    const buf  = await audioCtx.decodeAudioData(arr);
    sfxBuffers.set(url, buf);
    return buf;
  } catch { return null; }
}

function playSfx(url: string, volume = 1): void {
  const buf = sfxBuffers.get(url);
  if (!buf || audioCtx.state === 'suspended') return;
  try {
    const src  = audioCtx.createBufferSource();
    src.buffer = buf;
    const gain = audioCtx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    gain.connect(audioCtx.destination);
    src.start();
  } catch { /* ignore audio errors */ }
}

// Длительность анимации броска = длительность файла dice
let ROLL_ANIM_MS = 700;
loadSfx(diceSoundUrl).then(buf => {
  if (buf && isFinite(buf.duration) && buf.duration > 0) {
    ROLL_ANIM_MS = Math.round(buf.duration * 1000);
  }
});
loadSfx(pieceMoveUrl);
loadSfx(pieceOutUrl);
loadSfx(pieceAtHomeUrl);

function playDice():      void { playSfx(diceSoundUrl,    0.6); }
function playPieceMove(): void { playSfx(pieceMoveUrl,    0.5); }
function playPieceOut():  void { playSfx(pieceOutUrl,     0.7); }
function playPieceHome(): void { playSfx(pieceAtHomeUrl,  0.8); }

// Тип звука текущей анимации: определяется в doMove, используется в RAF-loop
type MoveSoundType = 'move' | 'out' | 'home' | 'silent';
let moveSoundType: MoveSoundType = 'silent';

// Обёртка над applyMove: вычисляет тип звука до анимации
function doMove(pieceId: number, nowMs: number): void {
  const player = state.currentPlayer;
  const prevPieces = state.pieces;
  state = applyMove(state, pieceId, nowMs);

  const movedPiece = state.pieces.find(p => p.player === player && p.id === pieceId)!;
  if (movedPiece.steps === 57) {
    moveSoundType = 'home';
  } else if (state.pieces.some((p, i) =>
    p.player !== player && p.steps === -1 && prevPieces[i].steps >= 0
  )) {
    moveSoundType = 'out';
  } else {
    moveSoundType = 'move';
    playPieceMove(); // первая клетка — сразу
  }
}

// ── Canvas setup ──────────────────────────────────────────────────────────
const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx    = canvas.getContext('2d')!;
let W = 0, H = 0;

function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (!w || !h) return; // Safari may briefly report 0 during transitions
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  W = w;
  H = h;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width  = `${W}px`;
  canvas.style.height = `${H}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
resize();
new ResizeObserver(resize).observe(document.body);

// iOS Safari evicts canvas backing store on memory pressure — force restore when page returns to foreground
document.addEventListener('visibilitychange', () => { if (!document.hidden) resize(); });
window.addEventListener('pageshow', (e) => { if (e.persisted) resize(); });

// ── State ─────────────────────────────────────────────────────────────────
let state: GameState = createInitialState();
const theme = readTheme();
let layout: Layout = computeLayout(W, H);
let pendingStart = true; // ждём первого жеста чтобы разблокировать AudioContext

// ── Intro sequence ────────────────────────────────────────────────────────
// introStep 0-3 = анимируем выход фишки 0 для каждого цвета; 4 = игра началась
const INTRO_COLORS: PlayerColor[] = ['red', 'green', 'yellow', 'blue'];
let introStep = 4; // сбрасывается в startIntro()

function isIntroActive(): boolean { return introStep < 4; }

function startIntro(): void {
  introStep = 0;
  track('game_start');
  scheduleIntroMove();
}

function scheduleIntroMove(): void {
  const delay = introStep === 0 ? 250 : 450;
  aiTimer = setTimeout(() => {
    aiTimer = null;
    const player = INTRO_COLORS[introStep];
    state = {
      ...state,
      currentPlayer: player,
      phase: 'moving',
      pieces: state.pieces.map(p =>
        p.player === player && p.id === 0 ? { ...p, steps: 0 } : p
      ),
      anim: {
        player,
        pieceId: 0,
        path: [-1, 0],
        stepIdx: 0,
        startMs: performance.now(),
        msPerStep: 130,
      },
      extraTurn: false,
      dice: { value: 0, rolling: false, used: false },
      selectablePieces: [],
    };
    moveSoundType = 'move';
    playPieceMove();
  }, delay);
}

// ── Меню (☰ → Продолжить / Заново / В хаб) ───────────────────────────────
let menuOpen = false;

function inRect(x: number, y: number, r: { x: number; y: number; w: number; h: number }): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function inCircle(x: number, y: number, c: { cx: number; cy: number; r: number }): boolean {
  return (x - c.cx) ** 2 + (y - c.cy) ** 2 <= (c.r + 6) ** 2;
}

// ── Debug mode ────────────────────────────────────────────────────────────
let debugMode = false;
let drag: DebugDrag | null = null;

function toCanvasCoords(clientX: number, clientY: number): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
}

function findPieceAtCell(
  row: number, col: number
): { player: PlayerColor; pieceId: number } | null {
  for (const piece of state.pieces) {
    if (piece.steps === -1) {
      const [pr, pc] = HOME_SLOTS[piece.player][piece.id];
      if (pr === row && pc === col) return { player: piece.player, pieceId: piece.id };
      continue;
    }
    const [pr, pc] = stepsToCoord(piece.player, piece.steps);
    if (pr === row && pc === col) return { player: piece.player, pieceId: piece.id };
  }
  return null;
}

function setDebugMode(on: boolean): void {
  debugMode = on;
  drag = null;
  clearAiTimer();
  introStep = 4; // прерываем интро если оно шло
  if (!on) {
    state = {
      ...state,
      phase: 'idle',
      anim: null,
      selectablePieces: [],
      extraTurn: false,
      dice: { value: 0, rolling: false, used: false },
    };
    if (state.currentPlayer !== 'red') {
      scheduleAi(triggerAiRoll, ROLL_DELAY);
    }
  }
}

// ── AI / общий таймер ─────────────────────────────────────────────────────
// Единственный слот таймера (последовательная цепочка ходов AI) — оборачиваем
// в scheduleAi/pauseAiTimer/resumeAiTimer, чтобы можно было честно
// приостановить отсчёт задержки пока открыто меню и возобновить с того же
// остатка времени (а не потерять ход или запустить его мгновенно).
let aiTimer: ReturnType<typeof setTimeout> | null = null;
let aiTimerFn: (() => void) | null = null;
let aiTimerStart = 0;
let aiTimerDelay = 0;

function scheduleAi(fn: () => void, delay: number): void {
  clearAiTimer();
  aiTimerFn = fn;
  aiTimerDelay = delay;
  aiTimerStart = performance.now();
  aiTimer = setTimeout(() => {
    aiTimer = null;
    aiTimerFn = null;
    fn();
  }, delay);
}

function clearAiTimer(): void {
  if (aiTimer) { clearTimeout(aiTimer); aiTimer = null; }
  aiTimerFn = null;
}

function pauseAiTimer(): void {
  if (aiTimer && aiTimerFn) {
    const elapsed = performance.now() - aiTimerStart;
    aiTimerDelay = Math.max(0, aiTimerDelay - elapsed);
    clearTimeout(aiTimer);
    aiTimer = null;
    // aiTimerFn остаётся живым — resumeAiTimer() перезапустит с остатком задержки
  }
}

function resumeAiTimer(): void {
  if (aiTimerFn) {
    scheduleAi(aiTimerFn, aiTimerDelay);
  }
}

// ── Пауза меню ────────────────────────────────────────────────────────────
let pauseStartMs: number | null = null;

function setMenuOpen(open: boolean): void {
  if (open === menuOpen) return;
  menuOpen = open;
  if (open) {
    pauseStartMs = performance.now();
    pauseAiTimer();
  } else if (pauseStartMs !== null) {
    const pausedMs = performance.now() - pauseStartMs;
    pauseStartMs = null;
    // Анимация ходит по абсолютному performance.now() относительно anim.startMs —
    // сдвигаем startMs на длительность паузы, чтобы после Resume не было скачка.
    if (state.anim) {
      state = { ...state, anim: { ...state.anim, startMs: state.anim.startMs + pausedMs } };
    }
    resumeAiTimer();
  }
}

const ROLL_DELAY    = 700;
const MOVE_DELAY    = 550;
const NO_MOVE_DELAY = 500;

// ── После завершения анимации ─────────────────────────────────────────────
function onAnimDone(): void {
  if (debugMode) return;

  // Интро: переходим к следующему игроку
  if (isIntroActive()) {
    introStep++;
    if (introStep < 4) {
      scheduleIntroMove();
    } else {
      // Все фишки выставлены — начинаем игру
      state = {
        ...state,
        phase: 'idle',
        currentPlayer: 'red',
        anim: null,
        extraTurn: false,
        dice: { value: 0, rolling: false, used: false },
        selectablePieces: [],
      };
    }
    return;
  }

  if (state.winner) {
    state = { ...state, phase: 'won' };
    setMenuOpen(false);
    track('game_over', { result: state.winner === 'red' ? 'win' : 'lose' });
    return;
  }

  if (state.extraTurn) {
    state = { ...state, phase: 'idle', anim: null, extraTurn: false, dice: { value: 0, rolling: false, used: false } };
    if (state.currentPlayer !== 'red') {
      scheduleAi(triggerAiRoll, ROLL_DELAY);
    }
    return;
  }

  state = nextTurn(state);
  if (state.currentPlayer !== 'red') {
    scheduleAi(triggerAiRoll, ROLL_DELAY);
  }
}

// ── Ход AI: бросок ────────────────────────────────────────────────────────
function triggerAiRoll(): void {
  aiTimer = null;
  if (debugMode || isIntroActive()) return;
  if (state.phase !== 'idle' || state.currentPlayer === 'red') return;

  state = rollDice(state);
  playDice();
  scheduleAi(() => {
    if (debugMode || isIntroActive()) return;
    state = finishRoll(state);

    if (state.selectablePieces.length === 0) {
      scheduleAi(() => {
        if (debugMode || isIntroActive()) return;
        state = nextTurn(state);
        if (state.currentPlayer !== 'red') {
          scheduleAi(triggerAiRoll, ROLL_DELAY);
        }
      }, NO_MOVE_DELAY);
    } else {
      scheduleAi(triggerAiMove, MOVE_DELAY);
    }
  }, ROLL_ANIM_MS);
}

// ── Ход AI: выбор фишки ───────────────────────────────────────────────────
function triggerAiMove(): void {
  aiTimer = null;
  if (debugMode || isIntroActive()) return;
  if (state.phase !== 'selecting' || state.currentPlayer === 'red') return;

  const pieceId = pickAiMove(state);
  if (pieceId === -1) {
    state = nextTurn(state);
    if (state.currentPlayer !== 'red') {
      scheduleAi(triggerAiRoll, ROLL_DELAY);
    }
    return;
  }

  doMove(pieceId, performance.now());
}

// ── Ход игрока: бросок ────────────────────────────────────────────────────
function triggerPlayerRoll(): void {
  if (isIntroActive() || state.phase !== 'idle' || state.currentPlayer !== 'red') return;

  state = rollDice(state);
  playDice();
  scheduleAi(() => {
    state = finishRoll(state);
    if (state.selectablePieces.length === 0) {
      scheduleAi(() => {
        state = nextTurn(state);
        if (state.currentPlayer !== 'red') {
          scheduleAi(triggerAiRoll, ROLL_DELAY);
        }
      }, NO_MOVE_DELAY);
    }
  }, ROLL_ANIM_MS);
}

// ── Обработка тапа ────────────────────────────────────────────────────────
function restartGame(): void {
  clearAiTimer();
  debugMode = false;
  drag = null;
  menuOpen = false;
  state = createInitialState();
  startIntro();
}

attachTapListener(canvas, ({ x, y }) => {
  layout = computeLayout(W, H);

  if (pendingStart) {
    audioCtx.resume();
    pendingStart = false;
    startIntro();
    return;
  }

  const menuVisible = !isIntroActive() && state.phase !== 'won';
  const m = menuRects(W, H);
  if (menuOpen) {
    if (inRect(x, y, m.resume))  { setMenuOpen(false); return; }
    if (inRect(x, y, m.restart)) { setMenuOpen(false); restartGame(); return; }
    if (inRect(x, y, m.hub))     { location.href = document.body.dataset.hubUrl ?? '/'; return; }
    setMenuOpen(false); return;   // тап мимо пунктов — закрыть
  }
  if (menuVisible && inCircle(x, y, m.toggle)) { setMenuOpen(true); return; }

  if (tapOnRestart(x, y, layout, W, H)) {
    restartGame();
    return;
  }

  if (tapOnDebug(x, y, layout, W, H)) {
    setDebugMode(!debugMode);
    return;
  }

  if (state.phase === 'won') {
    restartGame();
    return;
  }

  if (debugMode || isIntroActive()) return;

  if (state.currentPlayer !== 'red') return;

  if (state.phase === 'idle' && tapOnDice(x, y, layout, W, H)) {
    triggerPlayerRoll();
    return;
  }

  if (state.phase === 'selecting') {
    const cell = tapToCell(x, y, layout);
    if (!cell) return;

    for (const id of state.selectablePieces) {
      const piece = state.pieces.find(p => p.player === 'red' && p.id === id)!;
      const coords = piece.steps === -1
        ? HOME_SLOTS['red'][id]
        : stepsToCoord('red', piece.steps);

      if (coords[0] === cell.row && coords[1] === cell.col) {
        doMove(id, performance.now());
        return;
      }
    }
  }
});

// ── Debug drag: pointer-события ───────────────────────────────────────────
let pointerMoved = false;

canvas.addEventListener('pointerdown', (e) => {
  if (!debugMode) return;
  e.preventDefault();
  const { x, y } = toCanvasCoords(e.clientX, e.clientY);
  layout = computeLayout(W, H);
  const cell = tapToCell(x, y, layout);
  if (!cell) return;
  const found = findPieceAtCell(cell.row, cell.col);
  if (!found) return;
  pointerMoved = false;
  drag = { ...found, x, y };
  canvas.setPointerCapture(e.pointerId);
}, { passive: false });

canvas.addEventListener('pointermove', (e) => {
  if (!debugMode || !drag) return;
  e.preventDefault();
  const { x, y } = toCanvasCoords(e.clientX, e.clientY);
  pointerMoved = true;
  drag = { ...drag, x, y };
}, { passive: false });

canvas.addEventListener('pointerup', (e) => {
  if (!debugMode || !drag) return;
  e.preventDefault();
  const { x, y } = toCanvasCoords(e.clientX, e.clientY);
  layout = computeLayout(W, H);
  const cell = tapToCell(x, y, layout);

  if (cell && pointerMoved) {
    const newSteps = cellToSteps(drag.player, cell.row, cell.col);
    if (newSteps !== null) {
      const moved = drag;
      state = {
        ...state,
        pieces: state.pieces.map(p =>
          p.player === moved.player && p.id === moved.pieceId
            ? { ...p, steps: newSteps }
            : p
        ),
      };
    }
  }

  drag = null;
}, { passive: false });

canvas.addEventListener('pointercancel', () => { drag = null; });

// ── Game loop ─────────────────────────────────────────────────────────────
function loop(now: number): void {
  try {
    layout = computeLayout(W, H);

    if (!debugMode && !menuOpen && state.phase === 'moving' && state.anim) {
      const prevStepIdx = state.anim.stepIdx;
      const { state: next, done } = tickAnim(state, now);
      state = next;
      // Step-звук только для обычных ходов (не параллельно с piece_out / piece_at_home)
      if (!done && state.anim && state.anim.stepIdx > prevStepIdx && moveSoundType === 'move') {
        playPieceMove();
      }
      if (done) {
        if (moveSoundType === 'out')  playPieceOut();
        if (moveSoundType === 'home') playPieceHome();
        moveSoundType = 'silent';
        onAnimDone();
      }
    }

    const menuVisible = !pendingStart && !isIntroActive() && state.phase !== 'won';
    renderFrame(ctx, W, H, layout, state, theme, now, debugMode, drag, pendingStart, menuVisible, menuOpen);
  } catch (e) {
    console.error('[ludo] loop error', e);
  }
  requestAnimationFrame(loop);
}

requestAnimationFrame(loop);
